import React, { useState, useRef } from 'react';
import { FaInfo, FaRegEye, FaSpinner } from 'react-icons/fa6';
import { FaCamera, FaTimesCircle } from 'react-icons/fa';
import secureLocalStorage from "react-secure-storage";
import axios from 'axios';

export default function Register() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [photo, setPhoto] = useState(null);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const photoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const capitalize = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  // Input change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    const val = (name === 'firstName' || name === 'lastName') ? capitalize(value) : value;
    setFormData({ ...formData, [name]: val });
    setErrors({ ...errors, [name]: '' });
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters long.';
    }
    if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters long.';
    }
    if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters long.';
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (formData.password.length < 8) {
      newErrors.password = 'Blue Key must be at least 8 characters long.';
    }
    if (!photo) {
      newErrors.photo = 'Profile picture is required.';
    }
    if (!coverPhoto) {
      newErrors.coverPhoto = 'Cover photo is required.';
    }
    return newErrors;
  };

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData({ ...formData, password });
    setErrors({ ...errors, password: '' });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, photo: '' }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverPhoto(file);
    setCoverPreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, coverPhoto: '' }));
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const removeCover = () => {
    setCoverPhoto(null);
    setCoverPreview(null);
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: '', message: '' });
    const formErrors = validateForm();
    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) return;

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append('first_name', capitalize(formData.firstName.trim()));
      payload.append('last_name', capitalize(formData.lastName.trim()));
      payload.append('username', formData.username);
      payload.append('email', formData.email);
      payload.append('password', formData.password);
      if (photo) payload.append('photo', photo);
      if (coverPhoto) payload.append('cover_photo', coverPhoto);

      const response = await axios.post(
        'https://buzzinguniverse.com/backend/api/register-user',
        payload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setFeedback({ type: 'success', message: response.data.data.message });
      setFormData({ firstName: '', lastName: '', username: '', email: '', password: '' });
      removePhoto();
      removeCover();
    } catch (error) {
      const msg = error.response?.data?.message || 'Registration failed. Please check your details and try again.';
      setFeedback({ type: 'error', message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section>
      <div className="register-form-sec">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8 col-12">
              <div className="register-form-box">
                <div className="register-form-heading text-center">
                  <h2>Create an Account</h2>
                  <p className="text-muted">Join us today and enjoy exclusive benefits.</p>
                </div>
                <div className="registering-definitelly mb-4">
                  <ul className="d-flex align-items-center">
                    <li><FaInfo className="me-2" /></li>
                    <li>
                      <p>Registering is easy. Just fill in the fields below, and we’ll make a new account for you in no time.</p>
                    </li>
                  </ul>
                </div>
                <div className="register-main-form">
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="reg-photos-section mb-4">
                      <div className={`reg-cover-wrapper ${errors.coverPhoto ? 'reg-upload-error' : ''}`} onClick={() => coverInputRef.current?.click()}>
                        {coverPreview ? (
                          <img src={coverPreview} alt="Cover preview" className="reg-cover-img" />
                        ) : (
                          <div className="reg-cover-placeholder">
                            <FaCamera />
                            <span>Add Cover Photo</span>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={coverInputRef}
                          accept=".png,.jpg,.jpeg,.webp"
                          onChange={handleCoverChange}
                          hidden
                        />
                      </div>
                      <div className={`reg-avatar-wrapper ${errors.photo ? 'reg-upload-error' : ''}`} onClick={() => photoInputRef.current?.click()}>
                        {photoPreview ? (
                          <img src={photoPreview} alt="Profile preview" className="reg-avatar-img" />
                        ) : (
                          <div className="reg-avatar-placeholder">
                            <FaCamera />
                          </div>
                        )}
                        <input
                          type="file"
                          ref={photoInputRef}
                          accept=".png,.jpg,.jpeg,.webp"
                          onChange={handlePhotoChange}
                          hidden
                        />
                      </div>
                      {(errors.photo || errors.coverPhoto) && (
                        <div className="reg-photos-errors text-center mt-2">
                          {errors.coverPhoto && <p className="text-danger mb-1" style={{ fontSize: '13px' }}>{errors.coverPhoto}</p>}
                          {errors.photo && <p className="text-danger mb-0" style={{ fontSize: '13px' }}>{errors.photo}</p>}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="firstName" className="form-label">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                        value={formData.firstName}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.firstName}</div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="lastName" className="form-label">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                        value={formData.lastName}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.lastName}</div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="username" className="form-label">Username</label>
                      <input
                        type="text"
                        name="username"
                        className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                        value={formData.username}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.username}</div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        value={formData.email}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.email}</div>
                    </div>

                    <button
                      type="button"
                      onClick={generatePassword}
                    >
                      Generate Blue Key
                    </button>

                    <div className="password-box">
                      <label htmlFor="password" className="form-label">Choose a Blue Key</label>
                      <div className="password">
                        <input
                          type={passwordVisible ? "text" : "password"}
                          name="password"
                          className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                          value={formData.password}
                          onChange={handleChange}
                        />
                        <span
                          className="toggle-icon"
                          onClick={togglePasswordVisibility}
                          style={{ cursor: 'pointer' }}
                        >
                          <FaRegEye />
                        </span>
                        <div className="invalid-feedback">{errors.password}</div>
                      </div>
                    </div>
                    <div className="Complete-sign-up-btn">
                      <button type="submit" disabled={loading}>
                        {loading ? <FaSpinner className="loading-spinner" /> : 'Complete Sign Up'}
                      </button>
                    </div>
                    {feedback.message && (
                      <div className={`mt-3 alert alert-${feedback.type === 'success' ? 'success' : 'danger'}`}>
                        {feedback.message}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
